import { ChevronLeft, ChevronRight, Calendar, Wand2, Save, Loader2, User, X, Clock } from 'lucide-react';
import { parseLocalDate } from '../../hooks/useScheduleBuilder';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  primaryRole: string;
}

interface ScheduleBuilderHeaderProps {
  selectedWeek: { start: string; end: string };
  onNavigateWeek: (direction: 'prev' | 'next') => void;
  onGoToCurrentWeek: () => void;
  onGenerateFromTemplate?: () => void;
  onSave?: () => void;
  isDirty: boolean;
  isSaving: boolean;
  isGenerating?: boolean;
  // Employee filter props
  employees?: Employee[];
  selectedEmployeeId?: string | null;
  onSelectEmployee?: (employeeId: string | null) => void;
  employeeHours?: number;
  employeeShiftCount?: number;
}

export default function ScheduleBuilderHeader({
  selectedWeek,
  onNavigateWeek,
  onGoToCurrentWeek,
  onGenerateFromTemplate,
  onSave,
  isDirty,
  isSaving,
  isGenerating,
  employees,
  selectedEmployeeId,
  onSelectEmployee,
  employeeHours,
  employeeShiftCount,
}: ScheduleBuilderHeaderProps) {
  const selectedEmployee = employees?.find(e => e.id === selectedEmployeeId);
  // Format the week range for display
  const formatWeekRange = () => {
    const start = parseLocalDate(selectedWeek.start);
    const end = parseLocalDate(selectedWeek.end);

    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endDay = end.getDate();
    const year = end.getFullYear();

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}, ${year}`;
    }
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  };

  // Check if current week is selected
  const isCurrentWeek = () => {
    const now = new Date();
    const start = parseLocalDate(selectedWeek.start);
    const end = parseLocalDate(selectedWeek.end);
    return now >= start && now <= end;
  };

  return (
    <div className="flex flex-col gap-2 md:gap-3 pb-2 md:pb-4 border-b border-slate-200">
      {/* Week navigation - centered on mobile for cleaner look */}
      <div className="flex items-center justify-center md:justify-between gap-3">
        <div className="flex items-center gap-3 md:gap-3">
          <button
            onClick={() => onNavigateWeek('prev')}
            className="p-2 rounded-full md:rounded-lg border border-slate-200 hover:bg-slate-50 active:bg-slate-100 transition-colors"
            aria-label="Previous week"
          >
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>

          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-indigo-500 hidden md:block" />
            <span className="text-sm md:text-lg font-medium text-slate-800">{formatWeekRange()}</span>
          </div>

          <button
            onClick={() => onNavigateWeek('next')}
            className="p-2 rounded-full md:rounded-lg border border-slate-200 hover:bg-slate-50 active:bg-slate-100 transition-colors"
            aria-label="Next week"
          >
            <ChevronRight className="w-5 h-5 text-slate-500" />
          </button>

          {!isCurrentWeek() && (
            <button
              onClick={onGoToCurrentWeek}
              className="px-2.5 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-full hover:bg-indigo-100 active:bg-indigo-200 transition-colors"
            >
              Today
            </button>
          )}
        </div>

        {/* Action buttons - hidden on mobile, shown in top bar instead */}
        <div className="hidden md:flex items-center gap-2">
          {onGenerateFromTemplate && (
            <button
              onClick={onGenerateFromTemplate}
              disabled={isGenerating}
              className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 active:bg-slate-100 transition-colors disabled:opacity-50"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4" />
              )}
              <span>Generate from Template</span>
            </button>
          )}

          {onSave && (
            <button
              onClick={onSave}
              disabled={!isDirty || isSaving}
              className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>Save Changes</span>
            </button>
          )}
        </div>
      </div>

      {/* Employee filter - clean pill style on mobile */}
      {employees && onSelectEmployee && (
        <div className="flex items-center justify-center md:justify-start gap-2">
          <select
            value={selectedEmployeeId || ''}
            onChange={(e) => onSelectEmployee(e.target.value || null)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-full md:rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none md:min-w-[180px] appearance-none text-center md:text-left"
            style={{ backgroundImage: 'none' }}
          >
            <option value="">All Staff</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName.charAt(0)}.
              </option>
            ))}
          </select>

          {selectedEmployee && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-indigo-50 border border-indigo-100 rounded-full">
              <Clock className="w-3 h-3 text-indigo-500" />
              <span className="text-xs font-medium text-indigo-700">{employeeHours?.toFixed(1) || 0}h</span>
              <span className="text-xs text-indigo-400 hidden md:inline">({employeeShiftCount || 0})</span>
              <button
                onClick={() => onSelectEmployee(null)}
                className="p-0.5 hover:bg-indigo-100 rounded-full transition-colors ml-0.5"
              >
                <X className="w-3.5 h-3.5 text-indigo-500" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
